/* eslint-disable no-undef */
import utils from 'util/utils';
import { toState } from '../base/redux/functions';
import { getServerURL } from '../base/settings/functions';
import { addHashParamsToURL } from '../base/util/uri';

/**
 * Adds the current track state to the passed URL.
 *
 * @param {URL} url - The URL that will be modified.
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {URL} - Returns the modified URL.
 */
export function addTrackStateToURL(url, stateful) {
  const state = toState(stateful);
  const user = state['features/login'].user;
  const settings = state['features/settings'];
  const isVideoMuted = !settings.videoState;
  const isAudioMuted = settings.videoOnly;

  window.reload = isAudioMuted;
  return addHashParamsToURL(new URL(url), {
    // use new URL object in order to not pollute the passed parameter.
    // 'config.resolution': 2160,
    'userInfo.displayName': utils.getUsername(user),
    'config.startWithAudioMuted': isAudioMuted,
    'config.startWithVideoMuted': utils.isRemoteLogin(user)
      ? isVideoMuted
      : false
  });
}

/**
 * Retrieves the default URL for the app. This can either come from a prop to
 * the root App component or be configured in the settings.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState}
 * function.
 * @returns {string} - Default URL for the app.
 */
export function getDefaultURL(stateful) {
  const state = toState(stateful);
  // const { href } = window.location;

  // if (href) {
  //   return href;
  // }

  return getServerURL(state);
}

/**
 * Returns application name.
 *
 * @returns {string} The application name.
 */
export function getName() {
  return interfaceConfig.APP_NAME;
}

export function fetchUserConfig(config, user) {
  try {
    if (user?.device?.options?.videoResolutionOptions === 1) {
      if (user.device.options.preferredCodec === 'av1') {
        config.videoQuality.codecPreferenceOrder = [
          'AV1',
          'VP9',
          'VP8',
          'H264'
        ];
        config.p2p.codecPreferenceOrder = ['AV1', 'VP9', 'VP8', 'H264'];
      } else if (user.device.options.preferredCodec === 'av1-exclude-vp9') {
        config.videoQuality.codecPreferenceOrder = [
          'AV1',
          'VP8',
          'H264',
          'VP9'
        ];
        config.p2p.codecPreferenceOrder = ['AV1', 'VP8', 'H264', 'VP9'];
      } else if (user.device.options.preferredCodec === 'vp9') {
        config.videoQuality.codecPreferenceOrder = [
          'VP9',
          'VP8',
          'H264',
          'AV1'
        ];
        config.p2p.codecPreferenceOrder = ['VP9', 'VP8', 'H264', 'AV1'];
      } else if (user.device.options.preferredCodec === 'vp8') {
        config.videoQuality.codecPreferenceOrder = [
          'VP8',
          'H264',
          'AV1',
          'VP9'
        ];
        config.p2p.codecPreferenceOrder = ['VP8', 'H264', 'AV1', 'VP9'];
      } else if (user.device.options.preferredCodec === 'h264') {
        config.videoQuality.codecPreferenceOrder = [
          'H264',
          'AV1',
          'VP9',
          'VP8'
        ];
        config.p2p.codecPreferenceOrder = ['H264', 'AV1', 'VP9', 'VP8'];
      }

      if (user?.device?.options?.adaptiveMode === 1) {
        config.videoQuality.enableAdaptiveMode = true;
      } else if (user?.device?.options?.adaptiveMode === 0) {
        config.videoQuality.enableAdaptiveMode = false;
      }

      if (user?.device?.options?.svcSimulcast === 'svc') {
        if (!config.videoQuality.av1) config.videoQuality.av1 = {};
        if (!config.videoQuality.vp9) config.videoQuality.vp9 = {};
        config.videoQuality.av1.useSimulcast = false;
        config.videoQuality.vp9.useSimulcast = false;
        config.videoQuality.av1.useKSVC = true;
        config.videoQuality.vp9.useKSVC = true;
      } else if (user?.device?.options?.svcSimulcast === 'simulcast') {
        if (!config.videoQuality.av1) config.videoQuality.av1 = {};
        if (!config.videoQuality.vp9) config.videoQuality.vp9 = {};
        config.videoQuality.av1.useSimulcast = true;
        config.videoQuality.vp9.useSimulcast = true;
        config.videoQuality.av1.useKSVC = false;
        config.videoQuality.vp9.useKSVC = false;
      }

      if (user?.device?.options?.maxResolution === '180p') {
        config.resolution = 180;
        config.constraints.video.height.ideal = 180;
        config.constraints.video.height.max = 180;
        config.constraints.video.width.ideal = 320;
        config.constraints.video.width.max = 320;
      } else if (user?.device?.options?.maxResolution === '360p') {
        config.resolution = 360;
        config.constraints.video.height.ideal = 360;
        config.constraints.video.height.max = 360;
        config.constraints.video.width.ideal = 640;
        config.constraints.video.width.max = 640;
      } else if (user?.device?.options?.maxResolution === '720p') {
        config.resolution = 720;
        config.constraints.video.height.ideal = 720;
        config.constraints.video.height.max = 720;
        config.constraints.video.width.ideal = 1280;
        config.constraints.video.width.max = 1280;
      } else if (user?.device?.options?.maxResolution === '1080p') {
        config.resolution = 1080;
        config.constraints.video.height.ideal = 1080;
        config.constraints.video.height.max = 1080;
        config.constraints.video.width.ideal = 1920;
        config.constraints.video.width.max = 1920;
      } else if (user?.device?.options?.maxResolution === '1440p') {
        config.resolution = 1440;
        config.constraints.video.height.ideal = 1440;
        config.constraints.video.height.max = 1440;
        config.constraints.video.width.ideal = 2560;
        config.constraints.video.width.max = 2560;
      } else if (user?.device?.options?.maxResolution === '2160p') {
        config.resolution = 2160;
        config.constraints.video.height.ideal = 2160;
        config.constraints.video.height.max = 2160;
        config.constraints.video.width.ideal = 3840;
        config.constraints.video.width.max = 3840;
      }

      if (user?.device?.options?.minResolution === '180p') {
        config.constraints.video.height.min = 180;
        config.constraints.video.width.min = 320;
      } else if (user?.device?.options?.minResolution === '360p') {
        config.constraints.video.height.min = 360;
        config.constraints.video.width.min = 640;
      } else if (user?.device?.options?.minResolution === '720p') {
        config.constraints.video.height.min = 720;
        config.constraints.video.width.min = 1280;
      } else if (user?.device?.options?.minResolution === '1080p') {
        config.constraints.video.height.min = 1080;
        config.constraints.video.width.min = 1920;
      } else if (user?.device?.options?.minResolution === '1440p') {
        config.constraints.video.height.min = 1440;
        config.constraints.video.width.min = 2560;
      } else if (user?.device?.options?.minResolution === '2160p') {
        config.constraints.video.height.min = 2160;
        config.constraints.video.width.min = 3840;
      }
    }
  } catch (e) {
    console.error('Error fetching user config: ', e);
  }
  return config;
}
